/**
 * Process Visibility Settings Component
 * Manages entity-specific process visibility logging configuration
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Grid,
  Divider,
  InputAdornment,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  ExpandMore as ExpandMoreIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { DEFAULT_SETTINGS, PRESETS } from './processVisibilityConstants';

// eslint-disable-next-line max-lines-per-function
function ProcessVisibilitySettings({ entityId }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [originalSettings, setOriginalSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load current settings
  useEffect(() => {
    loadSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Check for changes
  useEffect(() => {
    const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const loadSettings = async () => {
    if (!entityId) return;

    try {
      setLoading(true);
      setError(null);

      const entityDoc = await getDoc(doc(db, 'entities', entityId));
      if (entityDoc.exists()) {
        const data = entityDoc.data();
        const processVisibilitySettings = data.settings?.processVisibility || {};

        // Merge with defaults
        const mergedSettings = {
          loggingEnabled: processVisibilitySettings.loggingEnabled ?? DEFAULT_SETTINGS.loggingEnabled,
          duplicateToCloudLogs: processVisibilitySettings.duplicateToCloudLogs ?? DEFAULT_SETTINGS.duplicateToCloudLogs,
          maxBatchSizeBytes: processVisibilitySettings.maxBatchSizeBytes ?? DEFAULT_SETTINGS.maxBatchSizeBytes,
          maxBatchCount: processVisibilitySettings.maxBatchCount ?? DEFAULT_SETTINGS.maxBatchCount,
          flushIntervalMilliseconds: processVisibilitySettings.flushIntervalMilliseconds ?? DEFAULT_SETTINGS.flushIntervalMilliseconds,
          maxMemoryMegabytes: processVisibilitySettings.maxMemoryMegabytes ?? DEFAULT_SETTINGS.maxMemoryMegabytes,
          minimumLogLevel: processVisibilitySettings.minimumLogLevel ?? DEFAULT_SETTINGS.minimumLogLevel
        };

        setSettings(mergedSettings);
        setOriginalSettings(mergedSettings);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!entityId || !hasChanges) {
      console.log('Save cancelled - entityId:', entityId, 'hasChanges:', hasChanges);
      return;
    }

    if (!user?.uid) {
      setError('User not authenticated');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      console.log('Saving settings for entity:', entityId);
      console.log('Settings to save:', settings);

      // Update entity document using dot notation for nested fields
      const updateData = {
        'settings.processVisibility.loggingEnabled': settings.loggingEnabled,
        'settings.processVisibility.duplicateToCloudLogs': settings.duplicateToCloudLogs,
        'settings.processVisibility.maxBatchSizeBytes': settings.maxBatchSizeBytes,
        'settings.processVisibility.maxBatchCount': settings.maxBatchCount,
        'settings.processVisibility.flushIntervalMilliseconds': settings.flushIntervalMilliseconds,
        'settings.processVisibility.maxMemoryMegabytes': settings.maxMemoryMegabytes,
        'settings.processVisibility.minimumLogLevel': settings.minimumLogLevel,
        'updatedAt': new Date(),
        'updatedBy': user.uid
      };

      console.log('Update data:', updateData);

      await updateDoc(doc(db, 'entities', entityId), updateData);

      console.log('Settings saved successfully');
      setOriginalSettings(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(`Failed to save settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    if (preset) {
      setSettings(preset.settings);
    }
  };

  const resetToDefaults = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const formatBytes = (bytes) => {
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${kb.toFixed(0)} KB`;
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms} ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = seconds / 60;
    return `${minutes.toFixed(1)} minutes`;
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully
        </Alert>
      )}
      {/* Debug Info */}
      {import.meta.env.DEV && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption">
            Debug Info: Entity ID: {entityId || 'Not set'} |
            User: {user?.uid || 'Not authenticated'} |
            Has Changes: {hasChanges.toString()} |
            Loading: {loading.toString()}
          </Typography>
        </Alert>
      )}
      {/* Preset Configurations */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SpeedIcon sx={{ mr: 1 }} />
          Quick Presets
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Apply a preset configuration optimized for specific use cases
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {Object.entries(PRESETS).map(([key, preset]) => (
            <Grid
              key={key}
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => applyPreset(key)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {preset.icon}
                  <Typography variant="subtitle2" sx={{ ml: 1 }}>
                    {preset.name}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {preset.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
      {/* Main Settings */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Process Visibility Configuration
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Basic Settings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Basic Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.loggingEnabled}
                      onChange={(e) => setSettings({ ...settings, loggingEnabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography>Enable Process Visibility Logging</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Master switch to enable/disable all process visibility logging
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.duplicateToCloudLogs}
                      onChange={(e) => setSettings({ ...settings, duplicateToCloudLogs: e.target.checked })}
                      color="primary"
                      disabled={!settings.loggingEnabled}
                    />
                  }
                  label={
                    <Box>
                      <Typography>Duplicate to Cloud Logs</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Also write logs to Google Cloud Logging for debugging
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>Minimum Log Level</InputLabel>
                  <Select
                    value={settings.minimumLogLevel}
                    onChange={(e) => setSettings({ ...settings, minimumLogLevel: e.target.value })}
                    label="Minimum Log Level"
                    disabled={!settings.loggingEnabled}
                  >
                    <MenuItem value="debug">Debug (All logs)</MenuItem>
                    <MenuItem value="info">Info (Normal operations)</MenuItem>
                    <MenuItem value="warn">Warning (Issues only)</MenuItem>
                    <MenuItem value="error">Error (Failures only)</MenuItem>
                  </Select>
                  <FormHelperText>
                    Only logs at or above this level will be recorded
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Performance Settings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Performance Tuning</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Max Batch Size"
                  type="number"
                  value={settings.maxBatchSizeBytes / 1024} // Show in KB
                  onChange={(e) => setSettings({
                    ...settings,
                    maxBatchSizeBytes: parseInt(e.target.value) * 1024
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">KB</InputAdornment>,
                  }}
                  helperText={`Flush logs when batch reaches this size (currently ${formatBytes(settings.maxBatchSizeBytes)})`}
                  disabled={!settings.loggingEnabled}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Max Batch Count"
                  type="number"
                  value={settings.maxBatchCount}
                  onChange={(e) => setSettings({
                    ...settings,
                    maxBatchCount: parseInt(e.target.value)
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">entries</InputAdornment>,
                  }}
                  helperText="Maximum number of log entries per batch"
                  disabled={!settings.loggingEnabled}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Flush Interval"
                  type="number"
                  value={settings.flushIntervalMilliseconds}
                  onChange={(e) => setSettings({
                    ...settings,
                    flushIntervalMilliseconds: parseInt(e.target.value)
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                  }}
                  helperText={`Auto-flush logs every ${formatDuration(settings.flushIntervalMilliseconds)}`}
                  disabled={!settings.loggingEnabled}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Max Memory Usage"
                  type="number"
                  value={settings.maxMemoryMegabytes}
                  onChange={(e) => setSettings({
                    ...settings,
                    maxMemoryMegabytes: parseInt(e.target.value)
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">MB</InputAdornment>,
                  }}
                  helperText="Force flush when memory usage exceeds this limit"
                  disabled={!settings.loggingEnabled}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Impact Analysis */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Configuration Impact</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              Current configuration analysis:
            </Alert>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Performance Impact</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {settings.duplicateToCloudLogs ?
                      '⚠️ Cloud logging enabled - may impact performance' :
                      '✅ Cloud logging disabled - better performance'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {settings.flushIntervalMilliseconds <= 1000 ?
                      '⚠️ Frequent flushes - higher overhead' :
                      '✅ Reasonable flush interval'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Visibility Impact</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Log level: {settings.minimumLogLevel} -
                    {settings.minimumLogLevel === 'debug' ? ' Maximum visibility' :
                     settings.minimumLogLevel === 'error' ? ' Minimal visibility' : ' Balanced visibility'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update frequency: Every {formatDuration(settings.flushIntervalMilliseconds)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Box>
            <Button
              variant="outlined"
              startIcon={<ResetIcon />}
              onClick={resetToDefaults}
              disabled={saving}
              sx={{ mr: 1 }}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSettings(originalSettings)}
              disabled={saving || !hasChanges}
            >
              Cancel Changes
            </Button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={saveSettings}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default ProcessVisibilitySettings;